// =============================================================================
// trains-tab.js – Renders the dedicated Trains tab with real GTFS timetables
// =============================================================================
// Shows departures from relevant train stations (S5, S6, RE4, RE5, R21, R23)
// grouped by station, with direction filter and next-train highlighting.
// =============================================================================

import { TRAIN_ROUTES, TRAIN_STATIONS, TRAIN_DEPARTURES } from "../data/trains.js";
import { rfiMonitorUrl } from "../data/train-rfi-links.js";
import { minsToHHMM, getDayType, escapeHtml } from "./utils.js";
import { patchDOM } from "./dom-utils.js";
import { openMap } from "./map.js";
import { getStopName } from "./line-config.js";
import { getLineData } from "./store";
import { calcInverseConnections } from "./inverse-connection";
import { deriveTrainTripStops } from "./train-trip-stops.js";

const TRAIN_DESTINATIONS = {
  to_milano: {
    S5: "Treviglio",
    S6: "Treviglio",
    RE_4: "Milano Centrale",
    RE4: "Milano Centrale",
    RE_5: "Milano Porta Garibaldi",
    RE5: "Milano Porta Garibaldi",
    R21: "Milano Porta Garibaldi",
    R23: "Milano Centrale"
  },
  from_milano: {
    S5: "Varese FS",
    S6: "Novara",
    RE_4: "Domodossola",
    RE4: "Domodossola",
    RE_5: "Porto Ceresio",
    RE5: "Porto Ceresio",
    R21: "Luino",
    R23: "Domodossola"
  }
};

let lastArgs = null;

const PIN_SVG = `<svg class="pin-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="width: 14px; height: 14px; vertical-align: middle; margin-left: 4px;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>`;

// P8: getTrainTripStops() now delegates to deriveTrainTripStops() which reads
// the real GTFS `TRAIN_TRIPS` stop_times table instead of the legacy
// hand-curated ROUTE_STATIONS_SEQ + synthetic Milano-station appends. This
// returns real per-trip stop sequences (Busto Arsizio → Rho Fiera →
// Milano Centrale, etc.) sourced directly from GTFS.
//
// The legacy ROUTE_STATIONS_SEQ + Milano-station supplement blocks below
// have been removed. The synthetic Terminal stop appends at the old tail
// of this function (Milano Certosa / Villapizzone / Lancetti / ... / Forlanini
// / Porta Garibaldi Superficie / Centrale) are now derived from the GTFS
// stop_times themselves.
export function getTrainTripStops(routeId, direction, dayType, startMin, activeStation) {
  return deriveTrainTripStops(routeId, direction, dayType, activeStation, startMin);
}

// Stations relevant per focus city
export const CITY_STATIONS = {
  BT: ["CN_FS", "PB_FS", "LG_FS"],
  VC: ["CN_FS", "PB_FS", "LG_FS"],
  DG: ["LG_FS", "CN_FS", "PB_FS"],
  AC: ["LG_FS", "PB_FS", "BS_FS", "BSN_FS"],
  CZ: ["PB_FS", "CN_FS", "VZ_FS"],
  LG: ["LG_FS", "CN_FS", "PB_FS", "BS_FS", "BSN_FS"],
  PB: ["PB_FS", "CN_FS", "LG_FS", "VZ_FS"],
  BS: ["BS_FS", "LG_FS", "CN_FS", "BSN_FS"]
};

// Default station order (all) dynamically derived
const ALL_STATIONS = Object.keys(TRAIN_STATIONS);

// Phase 3 of feature/figma-alignment: build the inline station timeline +
// RFI link shown when a train card is expanded. Reuses getTrainTripStops()
// (TRAIN_DEPARTURES inter-station matching) and adds a .pin-mini per stop
// whose code resolves in js/map.js trainMarkers (openMap(stationCode)).
function renderTrainCardExpanded(dep, direction, dayType, activeStation, currentMin) {
  const routeId = dep.routeId || dep.routeInfo?.name || "";
  if (!routeId) return "";
  const stops = getTrainTripStops(routeId, direction, dayType, dep.min, activeStation);
  if (!stops.length) return "";
  const lastIdx = stops.length - 1;
  return `<div class="station-timeline">
    ${stops
      .map((s, i) => {
        const isEnd = i === 0 || i === lastIdx;
        const code = s.code || "";
        const hasCoords = !!code && !!TRAIN_STATIONS[code];
        const pin = hasCoords
          ? `<button type="button" class="tl-pin map-trigger" data-stop-code="${escapeHtml(code)}" data-stop-name="${escapeHtml(s.name)}" aria-label="Apri ${escapeHtml(s.name)} sulla mappa" title="Apri sulla mappa"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></button>`
          : "";
        return `<div class="tl-row${isEnd ? " is-end" : ""}">
          <span class="tl-node"></span>
          <span class="tl-time">${minsToHHMM(s.min)}</span>
          <span class="tl-name">${escapeHtml(s.name)}</span>
          ${pin}
        </div>`;
      })
      .join("")}
  </div>`;
}

export function renderTrainsTab(state, cfg, saveFn) {
  const actualSaveFn = saveFn || lastArgs?.saveFn;
  lastArgs = { state, cfg, saveFn: actualSaveFn };
  const container = document.getElementById("trains-content");
  if (!container) return;

  const now = new Date();
  const currentMin = now.getHours() * 60 + now.getMinutes();
  // P8: allow user to peek at other day types via the Figma day-filter; falls back to today.
  const dayType = state.trainsDayType || getDayType(now, cfg);
  const focusCity = state.settings?.focusCity || cfg.defaults?.focusCity || "BT";
  const direction = state.trainsDirection || "to_milano";

  // Stazioni visibili: usa la scelta dell'utente (visibleTrains) se disponibile,
  // altrimenti fallback alle stazioni della città
  const userVisibleTrains = state.settings?.visibleTrains;
  const cityStations =
    Array.isArray(userVisibleTrains) && userVisibleTrains.length > 0
      ? userVisibleTrains.filter((code) => TRAIN_STATIONS[code])
      : CITY_STATIONS[focusCity] || ALL_STATIONS;
  const showAllStations = !!state.trainsShowAllStations;
  const visibleStations = showAllStations ? ALL_STATIONS : cityStations;
  const activeStation =
    state.trainsStation && visibleStations.includes(state.trainsStation)
      ? state.trainsStation
      : cityStations[0] || "CN_FS";

  const trainReachMinutes = state.settings?.stationReachMinutes?.[activeStation] || 0;

  let html = `
    <div class="page-head" style="--lc: var(--line-r21, var(--accent));">
      <h1>Treni</h1>
      <p>Trenord · S5, S6, RE4, RE5, R21, R23</p>
    </div>

    <div class="station-select" style="--lc: var(--line-r21, var(--accent));">
      <label>Stazione</label>
      <div class="station-buttons">
        ${visibleStations
          .map((code) => {
            const station = TRAIN_STATIONS[code];
            const isOutside = !cityStations.includes(code);
            const routesHere = Object.keys(TRAIN_DEPARTURES[code] || {})
              .map((r) => TRAIN_ROUTES[r]?.name || r)
              .join(", ");
            return `<button type="button" data-train-station="${code}" class="station-btn ${code === activeStation ? "active" : ""}${isOutside ? " line-outside" : ""}" title="${station?.name || code}">
              <span class="icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="3" width="16" height="16" rx="2"/><path d="M4 11h16M12 3v16"/><path d="M9 19l-2 2M15 19l2 2"/></svg></span>
              <div>
                <div class="name">${escapeHtml(station?.name || code)}</div>
                <div class="sub">${escapeHtml(code)}${routesHere ? ` · ${escapeHtml(routesHere)}` : ""}</div>
              </div>
              <svg class="chev" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>`;
          })
          .join("")}
      </div>
      <button type="button" class="text-btn line-scope-toggle" data-toggle-all-stations style="margin-top: 8px; font-size: 0.72rem;">
        ${showAllStations
          ? `← Solo le mie stazioni (${cityStations.length})`
          : `Tutte le stazioni (${ALL_STATIONS.length}) →`}
      </button>
    </div>

    <div class="filter-row" style="--lc: var(--line-r21, var(--accent));">
      <div class="day-filter">
        <button type="button" class="${dayType === "weekday" ? "active" : ""}" data-trains-day="weekday">Feriali</button>
        <button type="button" class="${dayType === "saturday" ? "active" : ""}" data-trains-day="saturday">Sabato</button>
        <button type="button" class="${dayType === "sunday" ? "active" : ""}" data-trains-day="sunday">Domenica</button>
      </div>
      <button type="button" class="direction-toggle" data-tdir="${direction === "to_milano" ? "from_milano" : "to_milano"}">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          ${direction === "to_milano"
            ? `<path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
            : `<path d="M12 7H2M6 3L2 7l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`}
        </svg>
        <span>${direction === "to_milano" ? "Per Milano" : "Da Milano"}</span>
      </button>
    </div>
  `;

  // Get departures for this station
  const stationData = TRAIN_DEPARTURES[activeStation];
  if (!stationData) {
    html += `<div class="empty-state">Nessun dato disponibile per questa stazione.</div>`;
    patchDOM(container, html, { onAfterPatch: () => bindTrainEvents(container) });
    return;
  }

  // Collect all departures for this station/direction/dayType
  const departures = [];
  for (const [routeId, schedules] of Object.entries(stationData)) {
    const key = `${dayType}_${direction}`;
    const times = schedules[key] || [];
    const routeInfo = TRAIN_ROUTES[routeId] || { name: routeId, color: "#666" };
    for (const min of times) {
      departures.push({ min, routeId, routeInfo });
    }
  }

  // Sort by time
  departures.sort((a, b) => a.min - b.min);

  let inverseConnMap = new Map();
  if (direction === "from_milano") {
    const lineData = getLineData();
    const invConnections = calcInverseConnections(
      activeStation,
      "from_milano",
      dayType,
      currentMin,
      lineData,
      cfg,
      state
    );
    for (const conn of invConnections) {
      const key = `${conn.trainArrivalMin}_${conn.trainLine}`;
      inverseConnMap.set(key, conn);
    }
  }

  if (departures.length === 0) {
    html += `<div class="empty-state">Nessuna partenza ${direction === "to_milano" ? "verso Milano" : "da Milano"} per ${dayType === "weekday" ? "oggi (feriale)" : dayType === "saturday" ? "sabato" : "domenica/festivo"}.</div>`;
    patchDOM(container, html, { onAfterPatch: () => bindTrainEvents(container) });
    return;
  }

  // Find next departure
  const nextIdx = departures.findIndex((d) => d.min >= currentMin);

  // Render upcoming departures (next 5)
  const upcoming = departures.filter((d) => d.min >= currentMin).slice(0, 5);
  if (upcoming.length > 0) {
    html += `
      <div class="trips" style="--lc: var(--line-r21, var(--accent));">
        <div class="section-head" style="margin-top:0">
          <span class="label">Prossime partenze</span>
          <span class="sim" title="Orario GTFS Trenord, non GPS">orario GTFS</span>
        </div>
        <div class="trips-list train-cards-list">`;
    for (let i = 0; i < upcoming.length; i++) {
      const dep = upcoming[i];
      const wait = dep.min - currentMin;
      // P8: Figma urgency tiers: urg-1 (≤2, "ora"), urg-2 (≤15, "soon"), urg-3 (>15, "calm")
      const urgencyClass = wait <= 2 ? "urg-1" : wait <= 15 ? "urg-2" : "urg-3";
      const dest = TRAIN_DESTINATIONS[direction]?.[dep.routeId] || "";

      // Derive arrival time at terminal from GTFS stop_times (last stop)
      let arrivalMin = null;
      try {
        const stopsSeq = deriveTrainTripStops(dep.routeId, direction, dayType, activeStation, dep.min);
        if (stopsSeq.length) arrivalMin = stopsSeq[stopsSeq.length - 1].min;
      } catch (_) { /* GTFS lookup miss is fine — wait-sub just hides */ }

      // leave-hint: only when reachMinutes > 0 and the wait window is tight enough.
      let leaveHintHtml = "";
      if (trainReachMinutes > 0 && wait > trainReachMinutes) {
        const leaveInMin = wait - trainReachMinutes;
        leaveHintHtml = `<span class="leave-hint">🚶 Esci tra <b>${leaveInMin} min</b> per prendere questo treno</span>`;
      }

      html += `<div class="train-card" data-trip-id="${i}-${dep.routeInfo.name}" style="--lc: ${dep.routeInfo.color};">
        <div class="top">
          <span class="line-tag" style="background: ${dep.routeInfo.color}; ${dep.routeInfo.color === "#f6d200" || dep.routeInfo.color === "#f39123" ? "color: #080B12;" : "color: #fff;"}">${escapeHtml(dep.routeInfo.name)}</span>
          <div>
            <div class="dep">${minsToHHMM(dep.min)}</div>
            ${dest ? `<div class="dest">per ${escapeHtml(dest)}</div>` : ""}
          </div>
          <div class="wait-stack">
            <div class="wait ${urgencyClass}">tra ${wait} min</div>
            ${arrivalMin != null ? `<div class="wait-sub">→ arr. ${minsToHHMM(arrivalMin)}</div>` : ""}
          </div>
          <svg class="chev" width="14" height="14" viewBox="0 0 14 14" fill="none" style="margin-left:8px" aria-hidden="true"><path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>
        ${leaveHintHtml}
        <div class="expand">
          ${renderTrainCardExpanded(dep, direction, dayType, activeStation)}
          <a class="rfi-link" href="${escapeHtml(rfiMonitorUrl(activeStation, TRAIN_STATIONS[activeStation]?.name))}" target="_blank" rel="noopener" data-rfi-station="${escapeHtml(TRAIN_STATIONS[activeStation]?.name || "")}">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M7 17L17 7M9 7h8v8"/></svg>
            Arrivi/Partenze in tempo reale (RFI)
          </a>
        </div>
      </div>`;
    }
    html += `</div>
      </div>`;
  }

  // Render full timetable
  const depCountLabel = `${departures.length} partenze ${direction === "to_milano" ? "→ Milano" : "← da Milano"}`;
  html += `
    <div class="section-title" style="margin-top: 12px; display: flex; justify-content: space-between; align-items: center; width: 100%;">
      <span>📅 Tabellone completo</span>
      <span style="font-size: 0.72rem; font-weight: 500; text-transform: none; color: var(--muted);">${depCountLabel}</span>
    </div>
    <div class="table-card"><div class="table-scroll">
      <table class="timetable train-timetable">
        <thead><tr><th>Orario</th><th>Linea</th><th>Attesa</th><th>Fermate</th></tr></thead>
        <tbody>`;

  for (let i = 0; i < departures.length; i++) {
    const dep = departures[i];
    const isPast = dep.min < currentMin;
    const isCurrent = i === nextIdx;
    const wait = dep.min - currentMin;
    const isExpanded = state.expandedTrains && state.expandedTrains.has(i);

    const dest = TRAIN_DESTINATIONS[direction]?.[dep.routeId] || "";
    const destHtml = dest
      ? `<div style="font-size: 0.72rem; color: var(--muted); margin-top: 2px;">per ${escapeHtml(dest)}</div>`
      : "";

    let reachHtml = "";
    if (trainReachMinutes > 0 && !isPast) {
      const reachMin = dep.min - trainReachMinutes;
      reachHtml = `<div style="font-size: 0.7rem; color: var(--quiet); font-weight: 500; margin-top: 2px; white-space: nowrap;">Parti: <strong style="color: var(--accent);">${minsToHHMM(reachMin)}</strong></div>`;
    }

    let invHtml = "";
    if (direction === "from_milano") {
      const connKey = `${dep.min}_${dep.routeInfo.name}`;
      const conn = inverseConnMap.get(connKey);
      if (conn) {
        invHtml = `<div class="train-inverse-conn" style="font-size: 0.72rem; color: var(--ok); margin-top: 2px; font-weight: 500;">
          → ${conn.busLine} alle ${minsToHHMM(conn.busDepMin)} &middot; <span class="${conn.state.css}">${conn.state.label}</span>
        </div>`;
      }
    }

    html += `<tr class="${isPast ? "past" : ""} ${isCurrent ? "current" : ""}">
      <td>
        <strong>${minsToHHMM(dep.min)}</strong>
        ${reachHtml}
      </td>
      <td>
        <span class="train-line-badge-sm" style="background: ${dep.routeInfo.color}; color: #fff;">${escapeHtml(dep.routeInfo.name)}</span>
        ${destHtml}
        ${invHtml}
      </td>
      <td>${isPast ? `<span class="muted">${Math.abs(wait)} min fa</span>` : `<span>${wait} min</span>`}</td>
      <td>
        <button type="button" class="text-btn train-expand-btn" data-train-index="${i}" style="font-size: 0.75rem; padding: 2px 6px; display: inline-flex; align-items: center; gap: 2px;">
          ${isExpanded ? "▲ Nascondi" : "▼ Fermate"}
        </button>
      </td>
    </tr>`;

    if (isExpanded) {
      const stops = getTrainTripStops(dep.routeId, direction, dayType, dep.min, activeStation);
      let timelineHtml = "";
      if (stops.length > 0) {
        let foundUpcoming = false;
        timelineHtml = stops
          .map((s) => {
            const isStopPast = s.min < currentMin;
            let statusClass = "";
            if (isStopPast) {
              statusClass = "past";
            } else if (!foundUpcoming) {
              statusClass = "upcoming";
              foundUpcoming = true;
            }

            const hasPin = s.isTrainStation;
            const stopEl = hasPin
              ? `<small class="map-trigger" data-stop-code="${s.code}" style="cursor:pointer" title="${escapeHtml(s.name)} - Vedi sulla mappa">${escapeHtml(s.name)} ${PIN_SVG}</small>`
              : `<small title="${escapeHtml(s.name)}">${escapeHtml(s.name)}</small>`;

            return `<div class="timeline-node ${statusClass}">
            <span></span>
            <strong>${minsToHHMM(s.min)}</strong>
            ${stopEl}
          </div>`;
          })
          .join("");
      } else {
        timelineHtml = `<div class="empty-mini">Impossibile caricare il dettaglio delle fermate.</div>`;
      }

      html += `<tr class="train-details-row">
        <td colspan="4" class="train-details-cell">
          <div class="timeline" style="margin-top: 4px; padding-left: 8px;">
            ${timelineHtml}
          </div>
        </td>
      </tr>`;
    }
  }

  html += `</tbody></table></div></div>`;

  // Station links
  const stationInfo = TRAIN_STATIONS[activeStation];
  if (stationInfo) {
    const rfiUrl = rfiMonitorUrl(activeStation, stationInfo.name);
    html += `<div class="app-footer">
      <a href="${escapeHtml(rfiUrl)}" target="_blank" rel="noopener" style="color: var(--accent);">🔴 Tabellone RFI tempo reale – ${escapeHtml(stationInfo.name)}</a>
    </div>`;
  }

  patchDOM(container, html, { onAfterPatch: () => bindTrainEvents(container) });
}

function bindTrainEvents(container) {
  const { state, cfg, saveFn } = lastArgs;

  // Inline reach minutes config change
  const inlineInput = container.querySelector("[data-station-inline-reach]");
  if (inlineInput && !inlineInput.__has_change) {
    inlineInput.__has_change = true;
    inlineInput.addEventListener("change", () => {
      const station = inlineInput.dataset.stationInlineReach;
      const min = Number(inlineInput.min || 0);
      const max = Number(inlineInput.max || 60);
      const value = Math.max(min, Math.min(max, Number(inlineInput.value || 0)));
      inlineInput.value = value;

      if (!state.settings) state.settings = {};
      if (!state.settings.stationReachMinutes) state.settings.stationReachMinutes = {};
      state.settings.stationReachMinutes[station] = value;

      if (saveFn) {
        saveFn({ stationReachMinutes: state.settings.stationReachMinutes });
      } else {
        renderTrainsTab(state, cfg);
      }
    });
  }

  // Station tabs
  container.querySelectorAll("[data-train-station]").forEach((btn) => {
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", () => {
      state.trainsStation = btn.dataset.trainStation;
      renderTrainsTab(state, cfg);
    });
  });

  // Direction toggle (also handles the new Figma .direction-toggle button — same data-tdir attribute)
  container.querySelectorAll("[data-tdir]").forEach((btn) => {
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", () => {
      state.trainsDirection = btn.dataset.tdir;
      renderTrainsTab(state, cfg);
    });
  });

  // P8: Day filter (Feriali / Sabato / Domenica) — overrides getDayType(now)
  container.querySelectorAll("[data-trains-day]").forEach((btn) => {
    if (btn.__has_day_click) return;
    btn.__has_day_click = true;
    btn.addEventListener("click", () => {
      state.trainsDayType = btn.dataset.trainsDay;
      renderTrainsTab(state, cfg);
    });
  });

  // Toggle "Show all stations"
  const toggleBtn = container.querySelector("[data-toggle-all-stations]");
  if (toggleBtn && !toggleBtn.__has_click) {
    toggleBtn.__has_click = true;
    toggleBtn.addEventListener("click", () => {
      state.trainsShowAllStations = !state.trainsShowAllStations;
      renderTrainsTab(state, cfg);
    });
  }

  // Train stop expand toggle
  container.querySelectorAll(".train-expand-btn").forEach((btn) => {
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", () => {
      const idx = parseInt(btn.dataset.trainIndex, 10);
      if (!state.expandedTrains) {
        state.expandedTrains = new Set();
      }
      if (state.expandedTrains.has(idx)) {
        state.expandedTrains.delete(idx);
      } else {
        state.expandedTrains.add(idx);
      }
      renderTrainsTab(state, cfg);
    });
  });

  // Map triggers
  container.querySelectorAll(".map-trigger").forEach((btn) => {
    if (btn.__has_click) return;
    btn.__has_click = true;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      openMap(btn.dataset.stopCode);
    });
  });

  // Phase 3+ of feature/figma-alignment: click on a .train-card toggles its
  // .expanded state via CSS (max-height transition on .train-card .expand).
  // Ignore clicks on inner interactive elements (pins, RFI links, leave-hint).
  container.querySelectorAll(".train-card[data-trip-id]").forEach((card) => {
    if (card.__has_expand_click) return;
    card.__has_expand_click = true;
    card.addEventListener("click", (e) => {
      if (e.target.closest(".map-trigger, .tl-pin, .rfi-link, .leave-hint, a, button:not(.train-card)")) return;
      // P8: CSS-only toggle for the .train-card .expand max-height transition.
      card.classList.toggle("expanded");
    });
  });
}
